import React, { useState, useEffect } from 'react';
import { SAPLayout } from '@/layouts/SAPLayout/SAPLayout';
import { mockSapSimulatorService } from '@/services/mock/MockSapSimulatorService';
import { SapGrnRecord } from '@/types/domain/sap-simulator';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { Layers } from 'lucide-react';

export const SapGrnPage: React.FC = () => {
  const [grns, setGrns] = useState<SapGrnRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadGrns = async () => {
      try {
        setIsLoading(true);
        const data = await mockSapSimulatorService.getGrns();
        setGrns(data.data);
      } finally {
        setIsLoading(false);
      }
    };
    loadGrns();
  }, []);

  const columns: ColumnDef<SapGrnRecord>[] = [
    {
      header: 'SAP GRN Number',
      accessorKey: 'grnNumber',
      cell: (row) => <span className="font-mono font-extrabold text-blue-600 text-xs">{row.grnNumber}</span>,
    },
    {
      header: 'PO Reference',
      accessorKey: 'poNumber',
      cell: (row) => <span className="font-mono font-bold text-emerald-600 text-xs">{row.poNumber}</span>,
    },
    {
      header: 'Vendor Code',
      accessorKey: 'vendorCode',
      cell: (row) => <span className="font-mono text-xs text-foreground font-semibold">{row.vendorCode}</span>,
    },
    {
      header: 'Material Code',
      accessorKey: 'materialCode',
      cell: (row) => <span className="font-mono text-xs text-primary font-extrabold">{row.materialCode}</span>,
    },
    {
      header: 'Ordered vs Received',
      cell: (row) => (
        <span className="text-xs font-bold text-foreground">
          {row.quantityReceived} / {row.quantityOrdered} Received ({row.quantityRejected} Rejected)
        </span>
      ),
    },
    {
      header: 'Receipt Date',
      accessorKey: 'receiptDate',
      cell: (row) => <span className="font-mono text-xs text-foreground-secondary">{row.receiptDate}</span>,
    },
    {
      header: 'Plant Code',
      accessorKey: 'plantCode',
      cell: (row) => <span className="font-mono text-xs text-blue-600 font-bold">{row.plantCode}</span>,
    },
  ];

  return (
    <SAPLayout>
      <div className="space-y-6 font-heading">
        <div>
          <h2 className="text-xl font-extrabold text-foreground flex items-center gap-2">
            <Layers className="h-5 w-5 text-blue-600" /> SAP Goods Receipts (MIGO) & Service Entries
          </h2>
          <p className="text-xs text-foreground-secondary font-medium">
            Simulated MIGO Goods Receipt postings verifying received quantities against PO line items.
          </p>
        </div>

        <DataTable data={grns} columns={columns} isLoading={isLoading} />
      </div>
    </SAPLayout>
  );
};
